/**
 * Three bars rising and falling — «здесь сейчас звучит голос».
 *
 * Replaces a speaker glyph wherever the mark has to be small. Lucide is drawn
 * at 24px with 2px strokes; `volume-2` is a cone plus two thin arcs, and at the
 * 10px a rail badge allows, those arcs land on sub-pixel strokes and smear into
 * a grey smudge. The icon isn't badly drawn — it is being asked to be a third
 * of the size it was designed for.
 *
 * Bars have no such floor: they are plain rectangles, so they stay crisp at any
 * size, and motion says "right now" far better than a static speaker ever did —
 * which is the actual message, since this marks a room that is live, not a
 * queue to catch up on.
 *
 * Motion is decoration, never the signal: with animation off (the in-app
 * "Снизить анимацию" toggle zeroes CSS durations globally) the bars rest at
 * full height and still read as a level meter.
 */
export function LiveBars(props: {
  /** Height of the tallest bar, px. Bar width and gap scale from it. */
  size?: number;
  className?: string;
}) {
  const size = props.size ?? 16;
  // Bar width tracks height so the mark keeps its proportions from a 10px rail
  // badge up to the 16px voice bar. Floored at 1.5px — a 1px bar disappears on
  // a non-retina screen.
  const bar = Math.max(1.5, Math.round((size / 8) * 2) / 2);
  const gap = Math.max(1, Math.round(bar / 2));
  return (
    <span
      className={`inline-flex items-end ${props.className ?? ''}`}
      style={{ height: size, gap }}
      aria-hidden
    >
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="origin-bottom rounded-full bg-current"
          style={{
            width: bar,
            height: '100%',
            // Staggered periods, none of them a multiple of another, so the
            // three never fall into lockstep and start reading as one blinking
            // block. Slow on purpose: this sits in peripheral vision.
            animation: `bk-eq ${0.9 + i * 0.25}s ease-in-out ${i * 0.15}s infinite`,
          }}
        />
      ))}
    </span>
  );
}
